import { Form, Head, usePage } from "@inertiajs/react"

import GoogleIcon from "@/components/google-icon"
import InputError from "@/components/input-error"
import TextLink from "@/components/text-link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import AuthLayout from "@/layouts/auth-layout"
import { newIdentityPasswordResetPath, signInPath } from "@/routes"

export default function Login() {
  const { cloud_identity } = usePage<{
    cloud_identity?: boolean
  }>().props

  // OmniAuth's request phase is a CSRF-protected POST (omniauth-rails_csrf_protection),
  // so this can't be an Inertia <Link> or a plain GET. Build a one-shot POST at
  // click time carrying the Rails authenticity token from the meta tag.
  const signInWithGoogle = () => {
    const token =
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") ?? ""
    const form = document.createElement("form")
    form.method = "post"
    form.action = "/auth/google_oauth2"
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "authenticity_token"
    input.value = token
    form.appendChild(input)
    document.body.appendChild(form)
    form.submit()
  }

  return (
    <AuthLayout
      title="Log in to your account"
      description={
        cloud_identity
          ? "Sign in with your Rebulk cloud email and password — accounts on this device are managed in the cloud portal"
          : "Enter your email and password below to log in"
      }
    >
      <Head title="Log in" />
      <Form
        method="post"
        action={signInPath()}
        resetOnSuccess={["password"]}
        className="flex flex-col gap-6"
      >
        {({ processing, errors }) => (
          <>
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  placeholder="email@example.com"
                />
                <InputError messages={errors.email} />
              </div>

              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">Password</Label>
                  <TextLink
                    href={newIdentityPasswordResetPath()}
                    className="ml-auto text-sm"
                  >
                    Forgot password?
                  </TextLink>
                </div>
                <Input
                  id="password"
                  type="password"
                  name="password"
                  required
                  autoComplete="current-password"
                  placeholder="Password"
                />
                <InputError messages={errors.password} />
              </div>

              <Button
                type="submit"
                className="mt-4 w-full"
                disabled={processing}
              >
                {processing && <Spinner />}
                Log in
              </Button>
            </div>
          </>
        )}
      </Form>

      <div className="flex flex-col gap-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-2">or</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={signInWithGoogle}
        >
          <GoogleIcon className="size-4" />
          Sign in with Google
        </Button>
      </div>
    </AuthLayout>
  )
}
